<template>
  <div ref="rootRef" class="relative inline-flex">
    <button
      ref="triggerRef"
      type="button"
      :class="triggerClass"
      :disabled="disabled"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="listbox"
      @click="toggle"
      @keydown.down.prevent="openAt(0)"
      @keydown.up.prevent="openAt(options.length - 1)"
      @keydown.enter.prevent="openAt(selectedIndex)"
      @keydown.space.prevent="openAt(selectedIndex)"
    >
      <slot name="trigger" :open="isOpen" />
    </button>

    <div
      v-if="isOpen"
      ref="panelRef"
      :class="panelClass"
      role="listbox"
      tabindex="-1"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.home.prevent="activeIndex = 0"
      @keydown.end.prevent="activeIndex = options.length - 1"
      @keydown.enter.prevent="selectActive"
      @keydown.space.prevent="selectActive"
      @keydown.escape.prevent="closeFromEscape"
    >
      <button
        v-for="(option, index) in options"
        :key="optionKey(option, index)"
        ref="optionRefs"
        type="button"
        :class="optionClass(option, index)"
        role="option"
        :aria-selected="isSelected(option) ? 'true' : 'false'"
        @click="select(option)"
        @mousemove="activeIndex = index"
      >
        <slot
          name="option"
          :option="option"
          :selected="isSelected(option)"
          :active="activeIndex === index"
        >
          {{ optionLabel(option) }}
        </slot>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: [String, Number],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  triggerClass: {
    type: [String, Array, Object],
    default: '',
  },
  align: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'right'].includes(value),
  },
  valueKey: {
    type: String,
    default: 'value',
  },
  labelKey: {
    type: String,
    default: 'label',
  },
})

const emit = defineEmits(['update:modelValue', 'select'])

const rootRef = ref(null)
const triggerRef = ref(null)
const panelRef = ref(null)
const optionRefs = ref([])
const isOpen = ref(false)
const activeIndex = ref(0)

const selectedIndex = computed(() => {
  const index = props.options.findIndex((option) => optionValue(option) === props.modelValue)
  return index === -1 ? 0 : index
})

const panelClass = computed(() => [
  'absolute top-full z-30 mt-2 max-h-72 min-w-48 overflow-auto border border-slate-200 bg-slate-50 p-1 shadow-lg shadow-slate-900/10 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:shadow-black/40',
  props.align === 'right' ? 'right-0' : 'left-0',
])

watch(isOpen, async (open) => {
  if (!open) return

  await nextTick()
  panelRef.value?.focus()
  focusActiveOption()
})

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) close()
  }
)

onMounted(() => {
  document.addEventListener('pointerdown', handleOutsidePointer)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleOutsidePointer)
})

function toggle() {
  if (props.disabled) return
  isOpen.value ? close() : openAt(selectedIndex.value)
}

function openAt(index) {
  if (props.disabled || !props.options.length) return

  activeIndex.value = clampIndex(index)
  isOpen.value = true
}

function close({ restoreFocus = true } = {}) {
  if (!isOpen.value) return

  isOpen.value = false
  if (restoreFocus) {
    nextTick(() => triggerRef.value?.focus())
  }
}

function closeFromEscape(event) {
  event.stopPropagation()
  close()
}

function handleOutsidePointer(event) {
  if (!isOpen.value || rootRef.value?.contains(event.target)) return
  close({ restoreFocus: false })
}

function move(delta) {
  if (!props.options.length) return

  activeIndex.value = clampIndex(activeIndex.value + delta)
  focusActiveOption()
}

function focusActiveOption() {
  nextTick(() => {
    const target = optionRefs.value[activeIndex.value]
    target?.focus()
  })
}

function selectActive() {
  const option = props.options[activeIndex.value]
  if (option) select(option)
}

function select(option) {
  emit('update:modelValue', optionValue(option))
  emit('select', option)
  close()
}

function optionValue(option) {
  if (option && typeof option === 'object') return option[props.valueKey]
  return option
}

function optionLabel(option) {
  if (option && typeof option === 'object') return option[props.labelKey]
  return String(option)
}

function optionKey(option, index) {
  return optionValue(option) ?? index
}

function isSelected(option) {
  return optionValue(option) === props.modelValue
}

function optionClass(option, index) {
  return [
    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-slate-700 outline-none focus:bg-indigo-50 focus:text-indigo-700 dark:text-slate-300 dark:focus:bg-indigo-500/10 dark:focus:text-indigo-300',
    activeIndex.value === index ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300' : '',
    isSelected(option) ? 'text-slate-950 dark:text-slate-100' : '',
  ]
}

function clampIndex(index) {
  if (!props.options.length) return 0
  if (index < 0) return props.options.length - 1
  if (index >= props.options.length) return 0
  return index
}
</script>
